import { useState } from "react";
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  Alert,
  Typography,
} from "@mui/material";
import adminAPI, { AdminUser } from "../admin.api";

interface UserEditTabProps {
  user: AdminUser;
  onSave: () => void;
  onError: (error: string | null) => void;
}

function UserEditTab({ user, onSave, onError }: UserEditTabProps) {
  const [formData, setFormData] = useState({
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    status: user.status,
    isAdmin: user.isAdmin || false,
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    onError(null);
    try {
      await adminAPI.updateUser(user._id, formData);
      onSave();
    } catch (err: any) {
      onError(err.response?.data?.error?.message || "Failed to update user");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* Info Box about User Management */}
      <Alert severity="info">
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          User Management Information
        </Typography>
        <Typography variant="body2" component="div">
          • <strong>Email:</strong> User's login email - changing this will affect their ability to log in
        </Typography>
        <Typography variant="body2" component="div">
          • <strong>Status:</strong> Active (can log in), Trialing (in trial period), Banned (cannot access), or other custom statuses
        </Typography>
        <Typography variant="body2" component="div">
          • <strong>Admin Flag:</strong> Grants full access to this admin panel and all user data
        </Typography>
        <Typography variant="body2" component="div">
          • <strong>Processor ID:</strong> Links user to payment processor (Paddle or PayKickstart) for subscription management
        </Typography>
        <Typography variant="body2" component="div" sx={{ mt: 0.5, fontStyle: 'italic' }}>
          ⚠️ Changes are immediate - ensure email is correct before saving
        </Typography>
      </Alert>

      <TextField
        label="Email"
        value={formData.email}
        onChange={(e) => handleChange("email", e.target.value)}
        fullWidth
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
          }
        }}
      />

      <TextField
        label="First Name"
        value={formData.firstName}
        onChange={(e) => handleChange("firstName", e.target.value)}
        fullWidth
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
          }
        }}
      />

      <TextField
        label="Last Name"
        value={formData.lastName}
        onChange={(e) => handleChange("lastName", e.target.value)}
        fullWidth
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
          }
        }}
      />

      <TextField
        select
        label="Status"
        value={formData.status}
        onChange={(e) => handleChange("status", e.target.value)}
        fullWidth
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
          }
        }}
      >
        <MenuItem value="active">Active</MenuItem>
        <MenuItem value="trialing">Trialing</MenuItem>
        <MenuItem value="cancelled">Cancelled</MenuItem>
        <MenuItem value="payment-failed">Payment Failed</MenuItem>
        <MenuItem value="banned">Banned</MenuItem>
        <MenuItem value="pending">Pending</MenuItem>
      </TextField>

      <FormControlLabel
        control={
          <Checkbox
            checked={formData.isAdmin}
            onChange={(e) => handleChange("isAdmin", e.target.checked)}
          />
        }
        label="Admin Access"
      />

      <Button 
        onClick={handleSave} 
        variant="contained" 
        disabled={loading}
        size="large"
        sx={{ 
          alignSelf: "flex-start",
          borderRadius: 2,
          px: 4,
          textTransform: 'none',
          fontWeight: 600
        }}
      >
        {loading ? "Saving..." : "Save Changes"}
      </Button>
    </Box>
  );
}

export default UserEditTab;